import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
  useWatch,
} from "react-hook-form";
import MinusIcon from "@/assets/icons/minus.svg";
import PlusIcon from "@/assets/icons/plus.svg";
import { Button } from "@/components/ui/button";
import { FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const layouts = {
  default: {
    formItemClasses: "",
    buttonClasses: "bg-brand-grey1 z-1 h-8 absolute border-0",
    inputClasses: " w-full px-12 text-center",
  },
  big: {
    formItemClasses: "",
    buttonClasses:
      "bg-brand-grey1 z-1 h-12 md:h-14 absolute w-12 px-0 border-0",
    inputClasses: " w-full px-14 text-center h-14 md:h-16",
  },
  medium: {
    formItemClasses: "",
    buttonClasses:
      "bg-brand-grey1 z-1 h-12 md:h-12 absolute w-12 px-0 border-0",
    inputClasses: " w-full px-14 text-center h-14",
  },
};

type FormCounterInputProps<T extends FieldValues> = {
  form: UseFormReturn<T, any, T>;
  fieldName: Path<T>;
  sufix?: string;
  layout?: keyof typeof layouts;
  fieldPlaceholder?: string;
  max?: number;
  min?: number;
  minTooltipContent?: string;
  maxTooltipContent?: string;
};

export const FormCounterInput = <T extends FieldValues>({
  form,
  fieldName,
  layout = "default",
  fieldPlaceholder,
  sufix,
  max,
  min = 1,
  minTooltipContent,
  maxTooltipContent,
}: FormCounterInputProps<T>) => {
  const { setValue, getValues } = form;
  const [inputValue, setInputValue] = useState(Number(getValues(fieldName)));
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("dateTime");
  const [openDecrement, setOpenDecrement] = useState(false);
  const [openIncrement, setOpenIncrement] = useState(false);
  const fieldWatcher = useWatch({ control: form.control, name: fieldName });

  // The form is the single source of truth: user actions write to it
  // directly and the local state only mirrors it for the text input. Syncing
  // both ways through two effects let them swap values on every render when
  // clicks outran the `useWatch` update, until React gave up (Sentry WEB-20Q).
  const commitValue = (value: number) => {
    setInputValue(value);
    setValue(fieldName, value as PathValue<T, Path<T>>);
  };

  const handleIncrement = () => {
    const currentDays = Number(getValues(fieldName)) || 0;
    if (max && currentDays >= max) return;
    commitValue(currentDays + 1);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(
          currentDays.toString().length,
          currentDays.toString().length
        );
      }
    }, 0);
  };

  const handleDecrement = () => {
    const currentDays = Number(getValues(fieldName)) || 0;
    if (max && currentDays <= 1) return;
    if (min && currentDays <= min) return;
    commitValue(currentDays - 1);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(
          currentDays.toString().length,
          currentDays.toString().length
        );
      }
    }, 0);
  };

  const formatDays = (value: string | number): string => {
    const number = parseInt(value.toString(), 10);
    return isNaN(number) ? "" : t(sufix ?? "", { count: number });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<T, Path<T>>
  ) => {
    const value = e.target.value.replace(/\D/g, "");

    setTimeout(() => {
      const cursorPos = value.length;

      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);

    if (max && Number(value) > max) {
      commitValue(max);
      return;
    }
    commitValue(Number(value));
  };

  // Mirrors changes made by other writers (calendar, length-discount stepper,
  // weekend toggles). One direction only: nothing here writes to the form.
  useEffect(() => {
    if (fieldWatcher >= min) {
      setInputValue(Number(fieldWatcher));
    }
  }, [fieldWatcher, min]);

  const handleInputBlur = () => {
    const value = Number(getValues(fieldName));
    if (isNaN(value) || value === 0) {
      setValue(fieldName, (min ?? 1) as PathValue<T, Path<T>>);
      return;
    }
    if (min && value < min) {
      commitValue(min);
    }
    if (max && value > max) {
      commitValue(max);
    }
  };

  const isDecrementDisabled = min ? Number(getValues(fieldName)) <= min : false;
  const isIncrementDisabled = max ? Number(getValues(fieldName)) >= max : false;

  return (
    <div className="relative flex items-center">
      <TooltipProvider delayDuration={0}>
        <Tooltip open={openDecrement}>
          <TooltipTrigger asChild>
            <Button
              className={cn(
                "left-1.5",
                layouts[layout].buttonClasses,
                isDecrementDisabled &&
                  "!pointer-events-auto !cursor-not-allowed opacity-50"
              )}
              data-pw="decrease-counter"
              onClick={handleDecrement}
              onKeyDown={(e) => {
                e.preventDefault();
                e.key === "Enter" && setOpenDecrement(!openDecrement);
              }}
              onMouseEnter={() => setOpenDecrement(true)}
              onMouseLeave={() => setOpenDecrement(false)}
              onTouchStart={() => setOpenDecrement(!openDecrement)}
              type="button"
              variant="outline"
            >
              <MinusIcon alt="-" height={2} width={17} />
            </Button>
          </TooltipTrigger>

          {isDecrementDisabled && (
            <TooltipContent className={minTooltipContent ? "" : "hidden"}>
              <span className="inline-block">{minTooltipContent}</span>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <FormField
        control={form.control}
        name={fieldName}
        render={({ field }) => (
          <FormItem className={cn("flex-1", layouts[layout].formItemClasses)}>
            <Input
              {...field}
              className={layouts[layout].inputClasses}
              data-pw="days-counter"
              onBlur={handleInputBlur}
              onChange={(e) => handleInputChange(e, field)}
              placeholder={fieldPlaceholder}
              ref={inputRef}
              type="text"
              value={inputValue && formatDays(inputValue)}
            />
          </FormItem>
        )}
      />
      <TooltipProvider delayDuration={0}>
        <Tooltip open={openIncrement}>
          <TooltipTrigger asChild>
            <Button
              className={cn(
                "right-1.5",
                layouts[layout].buttonClasses,
                isIncrementDisabled &&
                  "!pointer-events-auto !cursor-not-allowed opacity-50"
              )}
              data-pw="increase-counter"
              onClick={handleIncrement}
              onKeyDown={(e) => {
                e.preventDefault();
                e.key === "Enter" && setOpenIncrement(!openIncrement);
              }}
              onMouseEnter={() => setOpenIncrement(true)}
              onMouseLeave={() => setOpenIncrement(false)}
              onTouchStart={() => setOpenIncrement(!openIncrement)}
              type="button"
              variant="outline"
            >
              <PlusIcon alt="+" height={16} width={17} />
            </Button>
          </TooltipTrigger>
          {isIncrementDisabled && (
            <TooltipContent className={maxTooltipContent ? "" : "hidden"}>
              <span className="inline-block">{maxTooltipContent}</span>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
